import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { DynamicFormProps, FormAction } from './dynamic-form.types.ts';
import { createInitialValues, isFieldVisible } from './dynamic-form-utils.ts';
import { Form } from '@/components/ui/form.tsx';
import { Button } from '@/components/ui/button.tsx';
import { DynamicFormField } from './dynamic-form-field.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx';
import { StatusAlert } from '@/features/auth/components/status-alert.tsx';
import { LoaderIcon } from 'lucide-react';

export const DynamicForm = ({
  schema,
  onSubmit,
  onChange,
  isSubmitting = false,
}: DynamicFormProps) => {
  const initialValues = useMemo(
    () => (schema ? createInitialValues(schema.inputs) : {}),
    [schema]
  );

  const form = useForm({
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const formValues = form.watch();

  useEffect(() => {
    form.reset(initialValues);
  }, [initialValues]);

  useEffect(() => {
    if (onChange) {
      onChange(formValues);
    }
  }, [formValues, onChange]);

  if (!schema) {
    return (
      <div className="flex h-full min-h-[240px] w-full items-center justify-center rounded-md bg-gray-50 text-base">
        <span className="text-muted-foreground">No script found</span>
      </div>
    );
  }

  const handleSubmit = async (data: Record<string, unknown>) => {
    const visibleFields = Object.entries(schema.inputs)
      .filter(([, field]) => isFieldVisible(field, data))
      .map(([name]) => name);

    const submissionData = Object.fromEntries(
      Object.entries(data).filter(([key]) => visibleFields.includes(key))
    );

    await onSubmit(submissionData);
  };

  const handleActionClick = (action: FormAction) => {
    if (action.action === 'reset') {
      form.reset();
    }
  };

  const getStatusColor = (status: string | undefined) => {
    if (!status) return 'bg-gray-200';
    const statusLower = status.toLowerCase();
    if (statusLower === 'running' || statusLower === 'active') return 'bg-green-500';
    if (statusLower === 'stopped' || statusLower === 'error' || statusLower === 'failed')
      return 'bg-red-500';
    if (statusLower === 'paused' || statusLower === 'idle') return 'bg-amber-500';
    return 'bg-blue-500';
  };

  return (
    <Card className="flex h-full flex-col rounded-none border-none p-0 shadow-none">
      <CardHeader className="flex-shrink-0 px-0 py-0">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle>{schema.title}</CardTitle>
            {schema.description && <CardDescription>{schema.description}</CardDescription>}
          </div>
          <div className="flex items-center gap-2 rounded-md bg-gray-50 p-3 text-nowrap">
            <div className={`h-3 w-3 rounded-full ${getStatusColor(schema.status)}`}></div>
            <span className="text-sm font-medium">
              Status: {schema.status || 'Not available'}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col overflow-auto p-0 pt-2">
        {!schema.enabled && (
          <StatusAlert
            type="error"
            message="This script form is currently disabled."
            className="mb-2"
          />
        )}

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-1 flex-col justify-between space-y-6 px-0.5"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              {Object.entries(schema.inputs).map(([name, field]) => {
                const visible = isFieldVisible(field, formValues);

                if (!visible) return null;

                return <DynamicFormField key={name} name={name} field={field} />;
              })}
            </div>

            <div className="flex items-center gap-3 pt-4">
              {schema.actions?.submit ? (
                <Button
                  type="submit"
                  disabled={!schema.enabled || isSubmitting}
                  variant={schema.actions.submit.variant}
                >
                  {isSubmitting && (
                    <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
                  )}
                  {isSubmitting ? 'Executing' : schema.actions.submit.label}
                </Button>
              ) : (
                <Button type="submit" disabled={!schema.enabled || isSubmitting}>
                  {isSubmitting && (
                    <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
                  )}
                  {isSubmitting ? 'Executing' : 'Execute'}
                </Button>
              )}

              {schema.actions &&
                Object.entries(schema.actions)
                  .filter(([key]) => key !== 'submit')
                  .map(([key, action]) => (
                    <Button
                      key={key}
                      type={action.type || 'button'}
                      variant={action.variant}
                      disabled={!schema.enabled || isSubmitting}
                      onClick={() => handleActionClick(action)}
                    >
                      {action.label}
                    </Button>
                  ))}
              <div className="col-span-2 grid"></div>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
